import {
  FileText,
  LayoutDashboard,
  Menu,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Tooltip } from "@/components/ui/Tooltip";
import type { BootstrapPayload } from "@/domain/types";
import { apiRequest } from "@/lib/api";
import { cn } from "@/lib/cn";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/invoices/new", label: "New invoice", icon: FileText },
  { to: "/app/clients", label: "Clients", icon: Users },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="space-y-1">
      {navItems.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition",
              isActive
                ? "bg-brand-100 text-brand-800"
                : "text-ink-muted hover:bg-surface-muted hover:text-ink",
            )
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export function AdminShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [session, setSession] = useState<BootstrapPayload | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<BootstrapPayload>("/api/admin/bootstrap")
      .then((payload) => { if (!cancelled) setSession(payload); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-border bg-white/90 backdrop-blur-xl lg:hidden">
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4">
          <NavLink to="/app" aria-label="Ledgerly by Mark dashboard">
            <BrandLogo compact subtitle="Billing command center" />
          </NavLink>
          <Tooltip
            label={mobileOpen ? "Close navigation" : "Open navigation"}
            side="left"
          >
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-white text-ink"
              onClick={() => setMobileOpen((open) => !open)}
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </Tooltip>
        </div>
        {mobileOpen ? (
          <div id="mobile-navigation" className="border-t border-border px-4 py-3">
            <Navigation onNavigate={() => setMobileOpen(false)} />
          </div>
        ) : null}
      </header>

      <div className="mx-auto flex max-w-screen-2xl">
        <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-border bg-white p-5 lg:block">
          <NavLink to="/app" aria-label="Ledgerly by Mark dashboard">
            <BrandLogo subtitle="Cloud billing portal" />
          </NavLink>
          <div className="mt-8">
            <Navigation />
          </div>
          <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-ink p-4 text-white">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-300">Private ledger</p>
            <p className="mt-2 truncate text-sm font-bold text-white">{session?.workspace.name ?? "Loading workspace…"}</p>
            <p className="mt-1 truncate text-xs text-white/60">{session?.adminEmail ?? "Protected by Cloudflare Access"}</p>
          </div>
        </aside>
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
